import React from 'react'
import { useLocation } from 'react-router-dom'
import Navbar from '@/shared/components/Header/Navbar'
import AlphaIcon from '../components/AlphaIcon'
import { useRuntimeConfigContext } from '@/shared/providers/RuntimeConfigProvider'
import { MainLayoutProvider, useMainLayout } from '@/shared/providers/MainLayoutContext'

interface LayoutProps {
  children: React.ReactNode;
}

/**
 * Inner layout that consumes MainLayoutContext to apply overflow styles to <main>.
 * Separated so that MainLayoutProvider wraps it and useMainLayout() can be called here.
 */
const LayoutInner: React.FC<LayoutProps> = ({ children }) => {
  const { isFeatureEnabled } = useRuntimeConfigContext();
  const isAlphaEnabled = isFeatureEnabled('web.ccagent.feature.alphaBot');
  const location = useLocation();
  const { mainOverflowX } = useMainLayout();

  // When the user clicks a menu item for the page they're already on,
  // NavMenu sets location.state._mountKey to a unique timestamp.
  // Using it as the key on <main> forces a full remount of all page
  // components, resetting every piece of local state (filters, sort,
  // pagination, etc.) exactly as if the user navigated there fresh.
  const mountKey = (location.state as { _mountKey?: number } | null)?._mountKey;

  return (
    // `overflow-x-hidden` clips any wider chrome (e.g. the Navbar's flex row,
    // which has fixed-size children that don't shrink below ~1150 px) to the
    // viewport so child pages aren't pushed off-screen at narrow widths. The
    // dashboard's react-grid-layout owns its own width via a ResizeObserver
    // and will respond correctly to whatever room it's actually given.
    //
    <div className="flex h-screen w-full min-w-0 flex-col overflow-x-hidden bg-background">
      {/* Skip to main content — always available to keyboard users (WCAG 2.4.1) */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:fixed focus:top-2 focus:left-1/2 focus:-translate-x-1/2 focus:z-[220] focus:bg-primary focus:text-on-primary focus:px-5 focus:py-2 focus:rounded-md focus:shadow-lg focus:text-sm focus:font-medium focus:underline focus:underline-offset-2 focus:outline-none"
      >
        Skip to main content
      </a>
      <Navbar />
      <main
        key={mountKey}
        id="main-content"
        className="flex-1 flex flex-col min-h-0 overflow-y-auto"
        style={mainOverflowX ? { overflowX: mainOverflowX as React.CSSProperties['overflowX'] } : undefined}
      >

        {children}
      </main>
      {isAlphaEnabled && <AlphaIcon />}
    </div>
  );
};

export const Layout: React.FC<LayoutProps> = ({ children }) => {
  return (
    <MainLayoutProvider>
      <LayoutInner>{children}</LayoutInner>
    </MainLayoutProvider>
  );
};
